import _ from 'underscore';
import accounting from 'accounting';
import percent from 'percent';
import moment from 'moment-business-days';
import {percentageDifference, getMargin} from '../reporting/tools';
import aggregations from './aggregations';
import {transformBOLTardiness, getPickupOnTimeMetrics, getDeliveryOnTimeMetrics, getCanceledMetrics, getPhoneUseMetrics} from './formatters';

const formattedMetric = (metric, type) => {
    if (type === 'money') {
        return accounting.formatMoney(metric);
    } else if (type === 'percentage') {
        return percent.sign(metric);
    } else {
        return metric;
    }
};

const getAggregationMetric = (metric, key, agg) => {
    if (!metric) {
        return 0;
    } else if (!metric[key] && !_.isUndefined(metric.doc_count)) {
        return metric.doc_count;
    } else if (agg.aggregationType === 'bucket') {
        return metric[key]['buckets'][agg.filter].doc_count;
    } else {
        return metric[key].value || 0;
    }
};

export function transformFrequentShippers (period, metrics) {
    return _.pluck(metrics['frequent_shippers_' + period.name].frequent_shippers.buckets, 'key');
};

export function transformGrowthMetrics(periods, metrics) {

    const [period, comparisonPeriod] = periods;

    const aggregationsToSurface = _.omit(
        aggregations.growthAggregations,
        (agg) => {
            return agg.hide;
        }
    );

    const growthMetrics = _(aggregationsToSurface).map((agg, key) => {
        let metricInPeriod = getAggregationMetric(
            metrics[key + '_' + period.name],
            key,
            agg
        );
        let metricInComparisonPeriod = getAggregationMetric(
            metrics[key + '_' + comparisonPeriod.name],
            key,
            agg
        );
        let growthInMetric = percentageDifference(
            metricInPeriod,
            metricInComparisonPeriod
        );
        return {
            metric: agg.name,
            metricInPeriod: formattedMetric(metricInPeriod, agg.type),
            metricInComparisonPeriod: formattedMetric(metricInComparisonPeriod,
                agg.type
            ),
            growthInMetric
        }
    });

    return growthMetrics.length > 0 ? growthMetrics : [];
};

export function transformDailyLookbackMetrics(periods, metrics) {
    let data = {
        lookback: []
    };
    _(periods).map((period) => {
        let totalJobs = metrics['revenue_' + period.name].doc_count;
        let totalRevenue = metrics['revenue_' + period.name].revenue.value;
        let row = {
            date: moment(period.name, 'MM-DD-YYYY').format('L'),
            jobs: totalJobs
        };
        data.lookback.push(row);
    });
    return data;
};

export function transformDailyGrowthMetrics(periods, metrics) {
    _.extend(metrics, transformBOLTardiness(periods, metrics));
    _.extend(metrics, getPickupOnTimeMetrics(periods, metrics));
    _.extend(metrics, getDeliveryOnTimeMetrics(periods, metrics));
    _.extend(metrics, getCanceledMetrics(periods, metrics));
    _.extend(metrics, getPhoneUseMetrics(periods, metrics));

    return _(aggregations.dailyAggregations).map((agg, key) => {
        if(agg.hide)
            return false;

        let row = {
            metric: agg.name
        };

        _(periods).map((period) => {
            let rowKey = 'metric' + period.name;
            let metric = metrics[key + '_' + period.name];
            row[rowKey] = formattedMetric(
                getAggregationMetric(metric, key, agg),
                agg.type
            );
        });

        return row;
    }).filter((row) => {if(row !== false) return row});
};

export function transformDailyCustomerMetrics(periods, metrics) {
    let data = {
        shippers: {},
        carriers: {}
    };
    _(periods).map((period) => {
        let totalJobs = metrics['jobs_by_shipper_' + period.name].doc_count;
        let totalRevenue = metrics['revenue_' + period.name].revenue.value;

        let shippersInJobBuckets = metrics['jobs_by_shipper_' + period.name]['jobs_by_shipper'].buckets;
        let shippersInRevenueBuckets = metrics['revenue_by_shipper_' + period.name]['revenue_by_shipper'].buckets;
        data.shippers[period.name] = _(shippersInJobBuckets).map((shipper) => {
            let shipperInRevenueBucket = _.findWhere(shippersInRevenueBuckets, { key: shipper.key });
            return {
                customer: shipper.key,
                jobs: shipper.doc_count,
                percentageOfJobs: parseFloat(((shipper.doc_count/totalJobs)*100).toFixed(2)),
                revenue: shipperInRevenueBucket.revenue.value,
                percentageOfRevenue: parseFloat(((shipperInRevenueBucket.revenue.value/totalRevenue)*100).toFixed(2))
            }
        });

        let carriersInJobBuckets = metrics['jobs_by_carrier_' + period.name]['jobs_by_carrier'].buckets;
        let carriersInRevenueBuckets = metrics['revenue_by_carrier_' + period.name]['revenue_by_carrier'].buckets;
        data.carriers[period.name] = _(carriersInJobBuckets).map((carrier) => {
            let carrierInRevenueBucket = _.findWhere(carriersInRevenueBuckets, { key: carrier.key });
            return {
                customer: carrier.key,
                jobs: carrier.doc_count,
                percentageOfJobs: parseFloat(((carrier.doc_count/totalJobs)*100).toFixed(2)),
                revenue: carrierInRevenueBucket.revenue.value,
                percentageOfRevenue: parseFloat(((carrierInRevenueBucket.revenue.value/totalRevenue)*100).toFixed(2))
            }
        });
    });
    return data;
};

export function addCustomGrowthMetrics(periods, metrics) {
    const [period, comparisonPeriod] = periods;

    _.extend(metrics, transformBOLTardiness(periods, metrics));
    _.extend(metrics, getPickupOnTimeMetrics(periods, metrics));
    _.extend(metrics, getDeliveryOnTimeMetrics(periods, metrics));
    _.extend(metrics, getCanceledMetrics(periods, metrics));
    _.extend(metrics, getPhoneUseMetrics(periods, metrics));

    metrics['jobs_' + period.name] = {
        jobs: {
            value: metrics['revenue_' + period.name].doc_count
        }
    };
    metrics['jobs_' + comparisonPeriod.name] = {
        jobs: {
            value: metrics['revenue_' + comparisonPeriod.name].doc_count
        }
    };
    metrics['margin_' + period.name] = {
        margin: {
            value: getMargin(
                metrics['revenue_' + period.name].revenue.value,
                metrics['payouts_' + period.name].payouts.value
            )
        }
    };
    metrics['margin_' + comparisonPeriod.name] = {
        margin: {
            value: getMargin(
                metrics['revenue_' + comparisonPeriod.name].revenue.value,
                metrics['payouts_' + comparisonPeriod.name].payouts.value
            )
        }
    };
    metrics['jobs_per_day_' + period.name] = {
        jobs_per_day: {
            value: Math.ceil(metrics['revenue_' + period.name].doc_count/period.days)
        }
    };
    metrics['jobs_per_day_' + comparisonPeriod.name] = {
        jobs_per_day: {
            value: Math.ceil(metrics['revenue_' + comparisonPeriod.name].doc_count/comparisonPeriod.days)
        }
    };
    metrics['revenue_per_day_' + period.name] = {
        revenue_per_day: {
            value: metrics['revenue_' + period.name].revenue.value/period.days
        }
    };
    metrics['revenue_per_day_' + comparisonPeriod.name] = {
        revenue_per_day: {
            value: metrics['revenue_' + comparisonPeriod.name].revenue.value/comparisonPeriod.days
        }
    };

    return metrics;
};
